import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    MapPin, GlobeHemisphereWest, Bell, ArrowUpRight, Phone, ChatCircleDots,
    CheckCircle, ClockCounterClockwise, Clock, ShieldWarning, Siren, Microphone, Trash, PawPrint, ShieldCheck,
} from "@phosphor-icons/react";
import PageShell from "@/components/PageShell";
import api, { fileUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { timeAgoStr, scanLocation, scanLocationLabel } from "@/lib/petUtils";

export default function RescueCenter() {
    const { t } = useI18n();
    const [tab, setTab] = useState("alerts");
    const [notifs, setNotifs] = useState({ items: [], unread: 0 });
    const [scans, setScans] = useState([]);
    const [pets, setPets] = useState([]);
    const [petFilter, setPetFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const loadNotifs = async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotifs(data);
        } catch (_) { /* ignore */ }
    };

    const loadScans = async () => {
        try {
            const { data: p } = await api.get("/pets");
            setPets(p);
            const results = await Promise.all(p.map((pet) =>
                api.get(`/pets/${pet.id}/scans`).then((r) => r.data.map((s) => ({ ...s, pet }))).catch(() => [])
            ));
            const merged = results.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setScans(merged);
        } catch (_) { /* ignore */ }
    };

    useEffect(() => {
        (async () => {
            await Promise.all([loadNotifs(), loadScans()]);
            setLoading(false);
        })();
    }, []);

    const markAllRead = async () => {
        try { await api.post("/notifications/read-all"); } catch (_) { /* ignore */ }
        loadNotifs();
    };

    const markOneRead = async (id) => {
        try { await api.post(`/notifications/${id}/read`); } catch (_) { /* ignore */ }
        loadNotifs();
    };

    const deleteOne = async (id) => {
        // Optimistic: hide immediately, then confirm with the server.
        setNotifs((prev) => ({
            items: prev.items.filter((n) => n.id !== id),
            unread: Math.max(0, prev.unread - (prev.items.find((n) => n.id === id && !n.is_read) ? 1 : 0)),
        }));
        try { await api.delete(`/notifications/${id}`); } catch (_) { loadNotifs(); }
    };

    const filteredScans = petFilter === "all" ? scans : scans.filter((s) => s.pet_id === petFilter);

    return (
        <PageShell
            testId="rescue-page"
            kicker={t("nav.rescue_center")}
            title={t("rescue.title")}
            sub={t("rescue.sub")}
            actions={tab === "alerts" && notifs.unread > 0 && (
                <button
                    data-testid="rescue-mark-all"
                    onClick={markAllRead}
                    className="btn-outline text-sm w-full sm:w-auto"
                >
                    <CheckCircle size={14} weight="fill" />
                    <span className="ms-1.5">{t("dash.mark_all_read")}</span>
                </button>
            )}
        >
            {/* Family Safety Status — heroic per-pet cards */}
            {!loading && pets.length > 0 && (
                <section data-testid="rescue-family-safety" className="mb-8">
                    <div className={pets.length === 1 ? "" : "grid gap-4 md:grid-cols-2"}>
                        {pets.map((p) => (
                            <PetSafetyCard key={p.id} pet={p} t={t} solo={pets.length === 1} />
                        ))}
                    </div>
                </section>
            )}

            {/* Tabs */}
            <div
                data-testid="rescue-tabs"
                role="tablist"
                className="mb-6 inline-flex items-center gap-1 rounded-full border border-paw-line dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur p-1 shadow-soft"
            >
                <TabButton
                    active={tab === "alerts"}
                    onClick={() => setTab("alerts")}
                    icon={<Bell size={14} weight="fill" />}
                    label={t("rescue.tab_alerts")}
                    badge={notifs.unread}
                    testId="rescue-tab-alerts"
                />
                <TabButton
                    active={tab === "scans"}
                    onClick={() => setTab("scans")}
                    icon={<MapPin size={14} weight="fill" />}
                    label={t("rescue.tab_scans")}
                    badge={0}
                    testId="rescue-tab-scans"
                />
            </div>

            {loading ? (
                <p className="text-paw-ink2 dark:text-paw-bone/60" data-testid="rescue-loading">
                    {t("dash.loading")}
                </p>
            ) : tab === "alerts" ? (
                notifs.items.length === 0 ? (
                    <EmptyAlerts t={t} />
                ) : (
                    <ul className="space-y-3" data-testid="rescue-alerts-list">
                        {notifs.items.map((n) => (
                            <AlertItem key={n.id} n={n} t={t} onOpen={() => markOneRead(n.id)} onDelete={() => deleteOne(n.id)} />
                        ))}
                    </ul>
                )
            ) : (
                <>
                    {pets.length > 1 && (
                        <div
                            data-testid="rescue-scans-filter"
                            className="mb-5 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0"
                        >
                            <button
                                data-testid="rescue-scans-filter-all"
                                onClick={() => setPetFilter("all")}
                                aria-selected={petFilter === "all"}
                                className="chip-option flex-shrink-0"
                            >
                                {t("rescue.filter_all")}
                            </button>
                            {pets.map((p) => (
                                <button
                                    key={p.id}
                                    data-testid={`rescue-scans-filter-${p.id}`}
                                    onClick={() => setPetFilter(p.id)}
                                    aria-selected={petFilter === p.id}
                                    className="chip-option flex-shrink-0"
                                >
                                    {p.name}
                                </button>
                            ))}
                        </div>
                    )}
                    {filteredScans.length === 0 ? (
                        <EmptyScans t={t} />
                    ) : (
                        <ul className="space-y-3" data-testid="rescue-scans-list">
                            {filteredScans.map((s) => (
                                <ScanItem key={s.id} s={s} t={t} />
                            ))}
                        </ul>
                    )}
                </>
            )}
        </PageShell>
    );
}

/* ---------- Pet Safety Card — premium hero: big photo + dominant status + single strong action ---------- */
function PetSafetyCard({ pet, t, solo = false }) {
    const isLost = Boolean(pet.is_lost);
    const petName = String(pet.name || "").replace(/\b\p{L}/gu, (c) => c.toUpperCase());
    return (
        <article
            data-testid={`rescue-pet-card-${pet.id}`}
            className={`relative rounded-3xl overflow-hidden transition-shadow ${
                isLost
                    ? "shadow-[0_20px_60px_-20px_rgba(239,68,68,0.6)] ring-1 ring-red-500/40"
                    : "shadow-[0_12px_40px_-16px_rgba(0,0,0,0.5)] ring-1 ring-white/8"
            }`}
        >
            {/* Photo hero */}
            <div className={`relative w-full ${solo ? "aspect-[4/3] sm:aspect-[16/9]" : "aspect-[4/3]"} bg-gradient-to-br from-paw-forest/30 to-paw-terra/20`}>
                {pet.photo_path ? (
                    <img
                        src={fileUrl(pet.photo_path)}
                        alt={petName}
                        data-testid={`rescue-pet-photo-${pet.id}`}
                        // object-cover with center-top position keeps the pet's face
                        // and body visible in landscape frames without cropping the head.
                        // Previous default (50% 50%) on portrait photos in a 21:9 frame
                        // showed only the middle strip (bug: "only the dog's nose visible").
                        className="pet-photo absolute inset-0 w-full h-full object-cover object-[center_top]"
                    />
                ) : (
                    <div className="absolute inset-0 grid place-items-center">
                        <PawPrint size={72} weight="duotone" className="text-paw-terra/50" />
                    </div>
                )}
                {/* Legibility gradient */}
                <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

                {/* Status pill top-start */}
                <div
                    data-testid={`rescue-pet-status-${pet.id}`}
                    className={`absolute top-4 start-4 inline-flex items-center gap-1.5 rounded-full backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-lg ${
                        isLost
                            ? "bg-red-600/95 text-white ring-1 ring-red-400/60"
                            : "bg-emerald-500/95 text-white ring-1 ring-emerald-300/50"
                    }`}
                >
                    <span aria-hidden className={`w-1.5 h-1.5 rounded-full bg-white ${isLost ? "animate-pulse" : ""}`} />
                    {isLost ? t("pets_page.status_lost") : t("pets_page.status_active")}
                </div>

                {/* Name overlaid at bottom */}
                <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 text-white">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/75 font-semibold" dir="ltr">
                        {isLost ? "Emergency" : "Family Safety"}
                    </p>
                    <h3 className="mt-1 font-display text-2xl sm:text-3xl leading-none tracking-tight drop-shadow-md">
                        {petName}
                    </h3>
                </div>
            </div>

            {/* Dominant CTA */}
            <div className={`p-4 sm:p-5 ${isLost ? "bg-gradient-to-b from-red-50 to-white dark:from-red-950/40 dark:to-[hsl(152,15%,8%)]" : "bg-white dark:bg-paw-card"}`}>
                <Link
                    to={`/pets/${pet.id}/safety`}
                    data-testid={`rescue-pet-cta-${pet.id}`}
                    className={
                        isLost
                            ? "w-full inline-flex items-center justify-center gap-2 rounded-full bg-white dark:bg-white/10 hover:bg-paw-muted dark:hover:bg-white/15 px-6 py-3.5 text-sm sm:text-base font-semibold text-red-700 dark:text-red-300 border-2 border-red-400 dark:border-red-500/50 transition"
                            : "btn-emergency w-full text-sm sm:text-base py-3.5"
                    }
                >
                    {isLost ? (
                        <>
                            <ShieldCheck size={18} weight="fill" />
                            <span>{t("safety.mark_safe_cta").replace("{name}", petName)}</span>
                        </>
                    ) : (
                        <>
                            <Siren size={18} weight="fill" />
                            <span>{`${t("safety.activate_now")} — ${petName}`}</span>
                        </>
                    )}
                </Link>
            </div>
        </article>
    );
}

/* ---------- Tab button ---------- */
function TabButton({ active, onClick, icon, label, badge = 0, testId }) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={testId}
            onClick={onClick}
            className={`relative inline-flex items-center gap-1.5 rounded-full px-4 sm:px-5 py-2 text-sm font-semibold transition ${
                active
                    ? "bg-paw-forest text-white dark:bg-paw-terra shadow-soft"
                    : "text-paw-ink2 dark:text-paw-bone/60 hover:text-paw-ink dark:hover:text-paw-bone"
            }`}
        >
            {icon}
            <span>{label}</span>
            {badge > 0 && (
                <span className={`ms-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-paw-terra text-white"}`}>
                    {badge > 9 ? "9+" : badge}
                </span>
            )}
        </button>
    );
}

/* ---------- Alert item ---------- */
function AlertItem({ n, t, onOpen, onDelete }) {
    const { hasCoords, quality, mapHref } = scanLocation(n);
    const accLabel = scanLocationLabel(n, t);
    // Build a rich location line: street · neighborhood · city, region, country
    const primary = n.street
        ? [n.street, n.neighborhood].filter(Boolean).join(" · ")
        : n.neighborhood || null;
    const secondary = [n.city, n.region, n.country].filter(Boolean).join(", ") || null;
    const coordLine = hasCoords ? `${n.latitude.toFixed(5)}, ${n.longitude.toFixed(5)}` : null;
    const hasAnyLocation = Boolean(primary || secondary || coordLine);
    const chipTone = quality === "exact"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25"
        : quality === "coarse"
            ? "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/25"
            : "bg-paw-terra/10 text-paw-terra border-paw-terra/25";

    // Auto-mark as read the first time the card renders on screen (removes the red dot naturally)
    useEffect(() => {
        if (!n.is_read) {
            const timer = setTimeout(() => onOpen?.(), 900);
            return () => clearTimeout(timer);
        }
    }, [n.id, n.is_read, onOpen]);

    const confirmDelete = (e) => {
        e.stopPropagation();
        if (window.confirm(t("rescue.confirm_delete"))) onDelete?.();
    };

    return (
        <li
            data-testid={`rescue-alert-${n.id}`}
            className={`card-soft p-4 sm:p-5 relative ${n.is_read ? "" : "ring-1 ring-paw-terra/30"}`}
        >
            {!n.is_read && (
                <span className="absolute top-4 end-4 w-2 h-2 rounded-full bg-paw-terra" aria-label="unread" />
            )}
            <div className="flex items-start gap-3 sm:gap-4">
                <div className={`w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0 ${
                    n.type === "found"
                        ? "bg-paw-forest/12 text-paw-forest dark:text-paw-terra"
                        : n.type === "rescue_alert"
                            ? "bg-red-500/12 text-red-600"
                            : "bg-paw-terra/12 text-paw-terra"
                }`}>
                    {n.type === "found"
                        ? <Siren size={20} weight="fill" />
                        : n.type === "rescue_alert"
                            ? <Siren size={20} weight="fill" />
                            : (quality === "exact"
                                ? <MapPin size={20} weight="fill" />
                                : quality === "coarse"
                                    ? <ShieldWarning size={20} weight="fill" />
                                    : <GlobeHemisphereWest size={20} weight="duotone" />)}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-display text-base sm:text-lg text-paw-ink dark:text-paw-bone leading-snug pe-8">
                        {n.type === "found"
                            ? t("dash.notif_found", { finder: n.finder_name || "Someone", pet: n.pet_name })
                            : n.type === "rescue_alert"
                                ? t("rescue.rescue_alert_line", { pet: n.pet_name, km: n.distance_km ?? "?" })
                                : t("dash.notif_scan", { pet: n.pet_name })}
                    </p>
                    <p className="mt-0.5 text-xs text-paw-ink2 dark:text-paw-bone/60">
                        {timeAgoStr(n.created_at, t)}
                        <span className="opacity-70"> · {new Date(n.created_at).toLocaleString()}</span>
                    </p>
                    {hasAnyLocation && (
                        mapHref ? (
                            <a
                                href={mapHref}
                                target="_blank"
                                rel="noreferrer"
                                onClick={onOpen}
                                data-testid={`rescue-alert-location-${n.id}`}
                                className="mt-2 block rounded-xl border border-paw-line dark:border-white/10 bg-paw-muted/40 dark:bg-white/[0.03] hover:bg-paw-terra/8 dark:hover:bg-paw-terra/12 transition p-3 group"
                            >
                                <div className="flex items-start gap-2">
                                    <MapPin size={16} weight="fill" className="text-paw-forest dark:text-paw-terra mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0 text-left">
                                        {primary && <p className="text-sm font-semibold text-paw-ink dark:text-paw-bone truncate">{primary}</p>}
                                        {secondary && <p className={`text-xs text-paw-ink2 dark:text-paw-bone/60 ${primary ? "" : "font-semibold text-paw-ink dark:text-paw-bone"}`}>{secondary}</p>}
                                        {coordLine && !primary && !secondary && <p className="text-sm font-mono text-paw-ink dark:text-paw-bone">{coordLine}</p>}
                                        <p className="mt-1.5 flex items-center flex-wrap gap-1.5">
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${chipTone}`}>
                                                {accLabel.text}
                                            </span>
                                            <span className="text-[11px] text-paw-forest dark:text-paw-terra font-semibold group-hover:underline inline-flex items-center gap-1">
                                                {t("scanloc.open_in_maps")} <ArrowUpRight size={11} weight="bold" />
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </a>
                        ) : (
                            <p className="mt-1 text-sm text-paw-ink dark:text-paw-bone/90 truncate flex items-center flex-wrap gap-1.5">
                                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${chipTone}`}>
                                    {accLabel.text}
                                </span>
                            </p>
                        )
                    )}
                    {n.message && (
                        <p
                            data-testid={`rescue-alert-msg-${n.id}`}
                            className="mt-2 rounded-xl bg-paw-terra/8 dark:bg-paw-terra/12 border border-paw-terra/20 p-3 text-sm italic text-paw-ink dark:text-paw-bone"
                        >
                            &ldquo;{n.message}&rdquo;
                        </p>
                    )}
                    {n.finder_audio_path && (
                        <div
                            data-testid={`rescue-alert-voice-${n.id}`}
                            className="mt-2 rounded-xl bg-paw-forest/6 dark:bg-paw-terra/12 border border-paw-forest/20 dark:border-paw-terra/25 p-3"
                        >
                            <div className="flex items-center gap-2 text-xs font-semibold text-paw-forest dark:text-paw-terra mb-2">
                                <Microphone size={14} weight="fill" />
                                <span>{t("rescue.finder_voice_h")}</span>
                            </div>
                            <audio
                                controls
                                preload="metadata"
                                src={fileUrl(n.finder_audio_path)}
                                data-testid={`rescue-alert-voice-player-${n.id}`}
                                className="w-full"
                            />
                        </div>
                    )}
                    {n.finder_phone && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            <a
                                href={`tel:${n.finder_phone}`}
                                data-testid={`rescue-alert-call-${n.id}`}
                                className="btn-primary !py-2 !px-3.5 text-xs"
                                onClick={onOpen}
                            >
                                <Phone size={14} weight="fill" />
                                <span className="ms-1.5">{t("rescue.call_finder")}</span>
                            </a>
                            <a
                                href={`sms:${n.finder_phone}`}
                                data-testid={`rescue-alert-sms-${n.id}`}
                                className="btn-outline !py-2 !px-3.5 text-xs"
                                onClick={onOpen}
                            >
                                <ChatCircleDots size={14} weight="fill" />
                                <span className="ms-1.5">{t("rescue.sms_finder")}</span>
                            </a>
                        </div>
                    )}
                    {n.type === "rescue_alert" && n.public_id && (
                        <div className="mt-3">
                            <a
                                href={`/p/${n.public_id}`}
                                data-testid={`rescue-alert-open-${n.id}`}
                                onClick={onOpen}
                                className="btn-primary !py-2 !px-3.5 text-xs"
                            >
                                <Phone size={14} weight="fill" />
                                <span className="ms-1.5">{t("rescue.rescue_alert_open")}</span>
                            </a>
                            {n.type === "rescue_alert" && n.public_id && (
                                <span className="ms-2 inline-block text-[11px] font-bold bg-red-500/15 text-red-700 dark:text-red-300 px-2 py-1 rounded-full">
                                    🚨 Nearby
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={confirmDelete}
                    data-testid={`rescue-alert-delete-${n.id}`}
                    aria-label={t("rescue.delete_alert")}
                    title={t("rescue.delete_alert")}
                    className="flex-shrink-0 w-9 h-9 rounded-full grid place-items-center text-paw-ink2 dark:text-paw-bone/50 hover:bg-red-50 dark:hover:bg-red-900/25 hover:text-red-600 dark:hover:text-red-400 transition"
                >
                    <Trash size={16} weight="regular" />
                </button>
            </div>
        </li>
    );
}

/* ---------- Scan item ---------- */
function ScanItem({ s, t }) {
    const { hasCoords, quality, mapHref } = scanLocation(s);
    const accLabel = scanLocationLabel(s, t);
    const locLabel = s.city
        ? `${s.city}${s.region ? `, ${s.region}` : ""}${s.country ? `, ${s.country}` : ""}`
        : hasCoords ? `${s.latitude.toFixed(5)}, ${s.longitude.toFixed(5)}` : t("scanloc.unknown");
    const chipTone = quality === "exact"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25"
        : quality === "coarse"
            ? "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/25"
            : "bg-paw-terra/10 text-paw-terra border-paw-terra/25";
    const iconWrap = quality === "exact"
        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
        : quality === "coarse"
            ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
            : "bg-paw-terra/10 text-paw-terra";

    return (
        <li
            data-testid={`rescue-scan-${s.id}`}
            className="card-soft p-4 sm:p-5 flex items-start gap-3 sm:gap-4"
        >
            <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl grid place-items-center flex-shrink-0 ${iconWrap}`}>
                {quality === "exact"
                    ? <MapPin size={22} weight="fill" />
                    : quality === "coarse"
                        ? <ShieldWarning size={22} weight="fill" />
                        : <GlobeHemisphereWest size={22} weight="duotone" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-1.5">
                    <span className="font-display text-lg text-paw-ink dark:text-paw-bone truncate">{s.pet?.name}</span>
                    <span
                        data-testid={`rescue-scan-quality-${s.id}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${chipTone}`}
                    >
                        {accLabel.text}
                    </span>
                </div>
                <p className="mt-0.5 text-sm text-paw-ink dark:text-paw-bone/90 truncate">{locLabel}</p>
                <p className="mt-1 text-xs text-paw-ink2 dark:text-paw-bone/60 inline-flex items-center gap-1">
                    <Clock size={12} /> {timeAgoStr(s.created_at, t)}
                    <span className="ms-1 opacity-70">· {new Date(s.created_at).toLocaleString()}</span>
                </p>
                {mapHref && (
                    <a
                        href={mapHref}
                        target="_blank" rel="noreferrer"
                        data-testid={`rescue-scan-map-${s.id}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-paw-forest dark:text-paw-terra hover:underline"
                    >
                        {quality === "approx" ? t("scanloc.open_area")
                            : quality === "coarse" ? t("scanloc.open_coarse")
                                : t("scanloc.open_exact")}
                        <ArrowUpRight size={11} weight="bold" />
                    </a>
                )}
            </div>
            {s.pet?.photo_path && (
                <img
                    src={fileUrl(s.pet.photo_path)}
                    alt={s.pet.name}
                    className="hidden sm:block w-14 h-14 rounded-2xl object-cover flex-shrink-0"
                />
            )}
        </li>
    );
}

/* ---------- Empty states ---------- */
function EmptyAlerts({ t }) {
    return (
        <div className="card-soft p-10 sm:p-16 text-center" data-testid="rescue-empty-alerts">
            <div className="inline-grid place-items-center w-16 h-16 rounded-full bg-paw-muted dark:bg-white/5 text-paw-forest dark:text-paw-terra mb-4">
                <ClockCounterClockwise size={32} weight="duotone" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-paw-ink dark:text-paw-bone">
                {t("rescue.empty_alerts_h")}
            </h2>
            <p className="mt-2 text-paw-ink2 dark:text-paw-bone/60 max-w-md mx-auto">
                {t("rescue.empty_alerts_sub")}
            </p>
        </div>
    );
}

function EmptyScans({ t }) {
    return (
        <div className="card-soft p-10 sm:p-16 text-center" data-testid="rescue-empty-scans">
            <div className="inline-grid place-items-center w-16 h-16 rounded-full bg-paw-muted dark:bg-white/5 text-paw-forest dark:text-paw-terra mb-4">
                <MapPin size={32} weight="duotone" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-paw-ink dark:text-paw-bone">
                {t("rescue.empty_scans_h")}
            </h2>
            <p className="mt-2 text-paw-ink2 dark:text-paw-bone/60 max-w-md mx-auto">
                {t("rescue.empty_scans_sub")}
            </p>
        </div>
    );
}
